'use client';

import type { CSSProperties } from 'react';
import { useTranslations } from 'next-intl';
import { BestBuyHomeCta } from 'components/ring-pro/BestBuyPickup/BestBuyHomeCta';
import { RingProHeroBackground } from 'components/landing/RingProHero';
import heroBase from 'components/landing/RingProHero.module.css';
import { homeAssetSrc } from 'components/landing/v3/homeAssetSrc';
import v3 from 'components/landing/v3/v3.module.css';
import CustomLink from 'components/shared/CustomLink';
import { CustomImage } from 'components/shared/custom-image/CustomImage';
import { useMarketingBannerOffset } from 'components/shared/Header/MarketingBanner';
import { BEST_BUY_PICKUP_ENABLED } from 'utils/featureFlags';
import { getHomepageLenis } from 'utils/homepageLenis';
import { ringProUsCdnAsset } from '../ringProUsAsset';
import styles from './RingProUsHero.module.css';

const idmeLogo = homeAssetSrc('web_v2/ring-pro-us/hero/hero-idme.png');
const heroPoster = ringProUsCdnAsset('hero/hero-ring.png');

const scrollToMeasures = () => {
  const target = document.getElementById('ring-pro-us-measures');
  if (!target) return;
  const lenis = getHomepageLenis();
  if (lenis) {
    lenis.scrollTo(target, { offset: 0 });
    return;
  }
  target.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

/* Figma 2592:7794 "check-mark": an open ring with a check through it. */
const CheckIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 12.6167 13.8347"
    fill="currentColor"
    aria-hidden
  >
    <path d="M0.0247332 6.81343C0.36362 3.39071 3.68124 0.82256 7.08036 1.24918C7.73453 1.33111 8.36198 1.55555 8.97519 1.78133L8.57618 2.51833C8.484 2.58335 8.35352 2.46089 8.26268 2.4266C5.26435 1.30484 1.91199 2.96988 1.06054 6.04614C-0.07769 10.1569 3.7142 13.9203 7.81869 12.7598C11.1742 11.8112 12.7599 7.93875 11.0771 4.86828C11.0054 4.73691 10.6269 4.22568 10.6206 4.16378C10.6046 4.00035 10.9609 3.55236 11.0464 3.38537C12.5453 5.02414 12.9937 7.43019 12.2941 9.5312C11.3719 12.3011 8.56683 14.0979 5.66024 13.8031C2.78482 13.5115 0.279901 11.0849 0.0247332 8.19391C-0.00643909 7.84078 -0.0100016 7.16345 0.0247332 6.81343Z" />
    <path d="M11.6922 0.401232L6.14803 10.0642L3.41422 7.12153L3.41333 7.01822L4.0105 6.458L6.01443 8.63917L10.9352 0L11.6922 0.401232Z" />
  </svg>
);

/* Figma 2654:2166: 4.6 x 8 chevron. */
const CaretIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 4.63697 8"
    fill="currentColor"
    aria-hidden
  >
    <path d="M4.63697 3.99944C4.63473 3.8343 4.57573 3.70139 4.44669 3.57698L0.910698 0.151677C0.808884 0.0498651 0.686069 0 0.537918 0C0.2405 0 0 0.233443 0 0.531025C0 0.675646 0.0601231 0.812088 0.167382 0.917107L3.36214 3.9972L0.167382 7.07825C0.0612467 7.18439 0 7.31618 0 7.46546C0 7.76304 0.2405 8 0.537918 8C0.683825 8 0.808884 7.94776 0.910698 7.84722L4.44669 4.42191C4.57685 4.29399 4.63697 4.15995 4.63697 3.99944Z" />
  </svg>
);

const renderWithAsterisk = (text: string) => {
  if (!text.includes('*')) return text;
  const parts = text.split('*');
  return (
    <>
      {parts.map((part, i) => (
        <span key={i}>
          {part}
          {i < parts.length - 1 ? (
            <span className={styles.asterisk} aria-hidden>
              *
            </span>
          ) : null}
        </span>
      ))}
    </>
  );
};

/** Every flag defaults to true. Pass a partial to hide pieces. */
export type RingProUsHeroVisibility = {
  background?: boolean;
  hsaBadge?: boolean;
  verifierPills?: boolean;
  headline?: boolean;
  description?: boolean;
  buyCta?: boolean;
  exploreCta?: boolean;
  priceLine?: boolean;
  benefits?: boolean;
};

const DEFAULT_VISIBILITY: Required<RingProUsHeroVisibility> = {
  background: true,
  hsaBadge: true,
  verifierPills: true,
  headline: true,
  description: true,
  buyCta: true,
  exploreCta: true,
  priceLine: false,
  benefits: true,
};

/** Shipped US homepage: every hero piece on. */
export const RING_PRO_US_HERO_VISIBILITY: Required<RingProUsHeroVisibility> = {
  ...DEFAULT_VISIBILITY,
};

type RingProUsHeroProps = {
  /** Formatted base price, e.g. "$479". */
  price: string;
  'aria-label'?: string;
  hasCampaignAbove?: boolean;
  visibility?: RingProUsHeroVisibility;
};

/**
 * Ring PRO US homepage hero (Figma 2517:3299). Desktop copy is bottom-left:
 * headline, body, buttons, then HSA/FSA and ID.me / GovX. Optional price line
 * above buttons via `priceLine`. Phones/tablets: badge, headline, body, buttons.
 * `verifierPills: false` omits the verifier at every width; otherwise it is
 * hidden below 1200px, where it is not in the mobile frame.
 */
export const RingProUsHero = ({
  price,
  'aria-label': ariaLabel,
  hasCampaignAbove,
  visibility,
}: RingProUsHeroProps) => {
  const t = useTranslations('homepage');
  const bannerPx = useMarketingBannerOffset();
  const show = { ...DEFAULT_VISIBILITY, ...visibility };
  const benefits = ['sizingKit', 'returns', 'warranty'] as const;
  const showBadgeRow = show.hsaBadge || show.verifierPills;

  return (
    <section
      aria-label={ariaLabel}
      data-hero="ring-pro-us"
      data-variant="v1"
      data-header-theme="light"
      className={`${heroBase.section} ${
        hasCampaignAbove ? heroBase.campaignAbove : ''
      } ${styles.section} ${styles.v1}`}
      style={
        {
          '--hero-banner-h': `${bannerPx}px`,
          '--hero-copy-banner-h': `${hasCampaignAbove ? 32 : bannerPx}px`,
        } as CSSProperties
      }
    >
      {show.background ? (
        <div className={styles.mediaContainer}>
          <RingProHeroBackground posterSrc={heroPoster} />
          <div className={styles.topFade} aria-hidden />
        </div>
      ) : null}
      <div className={`${heroBase.bottomFade} ${styles.bottomFade}`} aria-hidden />

      <div className={styles.content}>
        <div className={styles.copy}>
          {showBadgeRow ? (
            <div className={styles.badgeRow}>
              {show.hsaBadge ? (
                <CustomLink
                  href="/hsa-fsa"
                  className={styles.badge}
                  data-buttontype="ring_pro_us_hero_hsa_fsa"
                >
                  <CheckIcon className={styles.badgeIcon} />
                  <span className={styles.badgeLabel}>
                    {t('homepage.ringProUs.hsaFsaBadge')}
                  </span>
                  <CaretIcon className={styles.badgeCaret} />
                </CustomLink>
              ) : null}

              {show.verifierPills ? (
                <CustomLink
                  href="/heroes"
                  className={`${styles.badge} ${styles.heroes}`}
                  data-buttontype="ring_pro_us_hero_heroes"
                >
                  <span className={styles.heroesLabel}>
                    {t('homepage.ringProUs.heroPricing.label')}
                  </span>
                  <CustomImage
                    className={styles.heroesIdme}
                    src={idmeLogo}
                    alt={t('homepage.ringProUs.heroPricing.idme')}
                    width={486}
                    height={174}
                  />
                  <span className={styles.heroesDivider} aria-hidden />
                  <span className={styles.heroesBrand}>
                    {t('homepage.ringProUs.heroPricing.govx')}
                  </span>
                  <CaretIcon className={styles.badgeCaret} />
                </CustomLink>
              ) : null}
            </div>
          ) : null}
          {show.headline ? (
            <h1 className={styles.title}>{t('homepage.ringProUs.headline')}</h1>
          ) : null}
          {show.description ? (
            <p className={styles.body}>
              {renderWithAsterisk(t('homepage.ringProUs.body'))}
            </p>
          ) : null}
          {show.priceLine ? (
            <p className={styles.priceLine}>
              <span>{t('homepage.ringProUs.priceLine', { price })}</span>
              <span className={styles.sep} aria-hidden>
                •
              </span>
              <span>{t('homepage.ringProUs.noSubscription')}</span>
            </p>
          ) : null}
          {show.buyCta || show.exploreCta ? (
            <div className={styles.ctaRow}>
              {show.buyCta ? (
                <CustomLink
                  href="/ring-pro/buy"
                  className={`${v3.pill} ${v3.pillPrimary} ${styles.cta}`}
                  data-buttontype="ring_pro_us_hero_buy_button"
                >
                  {t('homepage.ringProUs.cta.buy')}
                </CustomLink>
              ) : null}
              {show.exploreCta && BEST_BUY_PICKUP_ENABLED ? (
                // This hero only renders for region US, so no region check.
                <BestBuyHomeCta
                  className={`${v3.pill} ${v3.pillSecondary} ${styles.cta} ${styles.ctaGlass}`}
                />
              ) : show.exploreCta ? (
                <button
                  type="button"
                  className={`${v3.pill} ${v3.pillSecondary} ${styles.cta} ${styles.ctaGlass}`}
                  data-buttontype="ring_pro_us_hero_explore_button"
                  onClick={scrollToMeasures}
                >
                  {t('homepage.ringProUs.cta.explore')}
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      {show.benefits ? (
        <ul className={styles.benefits}>
          {benefits.map((key) => (
            <li key={key} className={styles.benefit}>
              <span className={styles.benefitTitle}>
                {t(`homepage.ringProUs.benefits.${key}.title`)}
              </span>
              <span className={styles.benefitSub}>
                {t(`homepage.ringProUs.benefits.${key}.sub`)}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
};

export default RingProUsHero;
